import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NavSignalsService } from '@core/services/nav.signals.service';
import { environment } from '@environments/environment';
import { AppService } from 'app/app.service';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { SignalsService } from '@shared/services/signals/signals.service';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';

@Component({
    selector: 'app-nav-menu',
    imports: [
        CommonModule,
        MenuModule,
        BadgeModule,
        OverlayBadgeModule,
        RouterModule
    ],
    templateUrl: './nav-menu.component.html',
    styleUrl: './nav-menu.component.scss'
})
export class NavMenuComponent implements OnInit {

  sidenavVisible = this.navSignalsService.sidenavVisible$;
  sidenavMaskVisible = this.navSignalsService.sidebarMaskVisible$;
  version: string = environment.appVersion;
  isAdminRole$?: boolean = false;
  destroy$ = new Subject<void>();
  menuItems: MenuItem[] = [];

  //Used to detect the first redirect after login
  private suppressInitialSelection = true;

  constructor(
    public navSignalsService: NavSignalsService,
    private siginalService: SignalsService,
    public appService: AppService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
  ) { }

  ngOnInit(): void {
    this.siginalService.isAdminRole$.pipe(
      takeUntil(this.destroy$),
      tap((role) => {
        this.isAdminRole$ = role;
        this.loadMenuItems();
      })
    ).subscribe();
  }

  loadMenuItems(): void {
    this.menuItems = [
      {
        id: '1',
        label: 'Flights',
        iconLight: '/assets/icons/aircraft/activeLight.svg',
        iconDark: "/assets/icons/aircraft/activeDark.svg",
        route: 'flights',
        visible: true,
        disabled: false,
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[0]);
        }
      },
      {
        id: '2',
        label: 'Analyze',
        icon: "pi pi-history",
        route: 'analyze',
        visible: true,
        disabled: false,
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[1]);
        }
      },
      {
        id: '3',
        label: 'Search',
        icon: 'pi pi-search',
        route: 'search',
        visible: true,
        disabled: false,
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[2]);
        }
      },
      {
        id: '4',
        label: 'Export',
        icon: 'pi pi-file-export',
        route: 'export',
        visible: true,
        disabled: false,
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[3]);
        }
      },
      {
        id: '5',
        label: 'Settings (Coming Soon)',
        icon: 'pi pi-cog',
        route: 'settings',
        visible: true,
        disabled: true,
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[4]);
        }
      },
      {
        id: '6',
        label: 'About',
        icon: 'pi pi-info-circle',
        route: 'about',
        visible: true,
        disabled: false,
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[5]);
        }
      },
      {
        id: '7',
        label: 'Admin',
        icon: 'pi pi-users',
        route: 'admin',
        disabled: !this.isAdminRole$, //User must have admin role to see this
        visible: this.isAdminRole$, //User must have admin role to see this
        selected: false,
        command: () => {
          this.onMenuClick(this.menuItems[6]);
        }
      }
    ];

    /**
     * Subscribe to router events to update the selected menu item based on the current route.
     * This helps allow navigation for for deep linking and when navigating within the search
     * view to ensure that if navigating within search on a sequence or flight details the
     * sidebar remains on the Search menu item. 
     */ 
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Skip logic on the first redirect after login, for deeplinks to sequence details
      if (this.suppressInitialSelection) {
        this.suppressInitialSelection = false;
        // Sidebar should always select Flights after login when deeplinked to sequence details
        this.updateSidebarSelection('/flights');
        return;
      }

      const from = sessionStorage.getItem('from') ? JSON.parse(sessionStorage.getItem('from') || '') : '';
      if (event.urlAfterRedirects.includes('/sequence') && from === '/search/result') {
        this.updateSidebarSelection('/search');
      }
      if (event.urlAfterRedirects.includes('/sequence') && from !== '/search/result') {
        this.updateSidebarSelection('/flights');
      }
      this.updateSidebarSelection(event.urlAfterRedirects);
    });

    this.updateSidebarSelection(this.router.url);
  }

  onMenuClick(item: MenuItem) {
    this.menuItems.forEach((_, idx) => {
      let index = this.menuItems?.findIndex((items) => items === item);
      this.menuItems[idx].selected = false;
      this.menuItems[index].selected = true;
    });
    this.sidenavVisible.set(false);
    this.sidenavMaskVisible.set(false);
    this.breadcrumbService.clearBreadcrumbs();
    this.navSignalsService.topBarTitle$.set(item.label || '');

    // Initialize app settings and version data if not present
    const appSettings = localStorage.getItem('appSettings');
    const versionData = localStorage.getItem('logbook.version.data');
    if (!appSettings) {
      this.appService.initializeAppSettings();
    }
    if(!versionData) {
      this.appService.initializeVersionData();
    }
    /** Remove 'from' session storage, this is used to determine the previous route
     * wehn navigating within the search results view.
    */
    sessionStorage.removeItem('from');
    sessionStorage.removeItem('export_activeIndex');
  }

  updateSidebarSelection(url: string): void {
    this.menuItems.forEach(item => {
      if (url.includes(item.route)) {
        this.menuItems.forEach(item => item.selected = false);
        item.selected = url.includes(item.route);
        this.sidenavMaskVisible.set(false);
        this.navSignalsService.topBarTitle$.set(item.label || '');
      }
    });
  }

  menuPanel = {
    root: {
      background: '{surface-0}',
      borderColor: 'transparent',
    },
    list: {
      padding: '8px'
    },
    item: {
      focusBackground: '{surface.100}',
      borderRadius: '0px 6px 6px 0px'
    }
  }
  
}
